import PropTypes from "prop-types";
import * as React from "react";
import { Product, SignType } from "../../../domain";
import { useCurrentUser } from "../../../globalState";
import {
  useInfluencer,
  useMediaQuery,
  useSessionStorage,
} from "../../../hooks";
import {
  Accordion,
  AddIcon,
  Alert,
  Button,
  Chip,
  FAB,
  SearchInput,
  SectionHead,
  Spinner,
} from "../../../themeV2";
import BulkActionsToolbar from "./BulkActionsToolbar";
import List from "./List";
import css from "./Products.module.css";
import useBulkSelection from "./useBulkSelection";
import signTypeColor from "../../SignTypeChip/signTypeColor";

const signTypeOptions = SignType.list();

const ShopBuilderProducts = React.memo(function ShopBuilderProducts({
  route,
  setAddingProduct,
  salesCountMap,
  onClose,
}) {
  const { isAdmin } = useCurrentUser();
  const selection = useBulkSelection();
  const isCompact = useMediaQuery("(max-width: 600px)");

  const [savedUnselected, setSavedUnselected] = useSessionStorage(
    "STREAMILY::PRODUCT::FILTER",
    {},
  );

  const [selectedTypes, setSelectedTypes] = React.useState(
    () => new Set(signTypeOptions),
  );
  const [searchQuery, setSearchQuery] = React.useState("");

  React.useEffect(() => {
    const unselectedKeys = Object.keys(savedUnselected);
    if (unselectedKeys.length === 0) {
      setSelectedTypes(new Set(signTypeOptions));
    } else if (unselectedKeys.length === signTypeOptions.length) {
      setSelectedTypes(new Set());
    } else {
      setSelectedTypes(
        new Set(signTypeOptions.filter((t) => !savedUnselected[t])),
      );
    }
  }, [savedUnselected]);

  const toggleType = React.useCallback(
    (type) => {
      setSelectedTypes((prev) => {
        const next = new Set(prev);
        if (next.has(type)) {
          next.delete(type);
        } else {
          next.add(type);
        }
        // Persist as unselected map
        const unselected = {};
        signTypeOptions.forEach((t) => {
          if (!next.has(t)) {
            unselected[t] = t;
          }
        });
        setSavedUnselected(unselected);
        return next;
      });
    },
    [setSavedUnselected],
  );

  const { influencer, isLoading, isError, refresh } = useInfluencer({
    route,
    forShopBuilder: true,
  });

  const onClickAdd = React.useCallback(() => {
    setAddingProduct(true);
  }, [setAddingProduct]);

  const matchesSearch = React.useCallback(
    (product) => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return product.name?.toLowerCase().includes(q);
    },
    [searchQuery],
  );

  const activeProductFilter = React.useCallback(
    (product) =>
      selectedTypes.has(product.signedType) &&
      product.isActive &&
      !product.isArchived &&
      matchesSearch(product),
    [selectedTypes, matchesSearch],
  );

  const inactiveProductFilter = React.useCallback(
    (product) =>
      selectedTypes.has(product.signedType) &&
      (!product.isActive || product.isArchived) &&
      matchesSearch(product),
    [selectedTypes, matchesSearch],
  );

  const isFiltered = React.useMemo(
    () => selectedTypes.size !== signTypeOptions.length,
    [selectedTypes],
  );

  const handleClearFilters = React.useCallback(() => {
    setSavedUnselected({});
  }, [setSavedUnselected]);

  const activeCount = React.useMemo(
    () =>
      influencer?.products?.filter((p) => p.isActive && !p.isArchived).length ??
      0,
    [influencer],
  );

  const inactiveCount = React.useMemo(() => {
    if (!influencer?.products?.length) return 0;
    return influencer.products.filter((p) => !p.isActive || p.isArchived)
      .length;
  }, [influencer]);

  const handleReorder = React.useCallback(
    async ({ product, newPosition }) => {
      await Product.updatePosition({
        product,
        shopInfluencerId: influencer.influencerId,
        position: newPosition,
      });
      refresh();
    },
    [influencer, refresh],
  );

  if (isLoading) {
    return (
      <div style={{ padding: "var(--space-8)", textAlign: "center" }}>
        <Spinner />
      </div>
    );
  }

  if (isError) {
    return <Alert severity="error">Unable to load shop products</Alert>;
  }

  const productFilter = (
    <div className={css.toolbar}>
      <div className={css.searchInput}>
        <SearchInput
          placeholder="Search products..."
          value={searchQuery}
          onValueChange={setSearchQuery}
          aria-label="Search products"
        />
      </div>

      <div className={css.filterChips}>
        {signTypeOptions.map((type) => {
          const bgColor = signTypeColor(type);
          const isSelected = selectedTypes.has(type);
          const isLivestream = type === SignType.livestream;
          const label = SignType.toLabel(type);
          return (
            <Chip
              key={type}
              variant="filter"
              size="sm"
              label={label}
              selected={isSelected}
              onClick={() => toggleType(type)}
              className={
                isLivestream && isSelected ? css.livestreamChip : undefined
              }
              data-label={isLivestream ? label : undefined}
              style={
                isSelected && bgColor
                  ? { backgroundColor: bgColor, color: "#fff" }
                  : undefined
              }
            />
          );
        })}
      </div>

      {isFiltered && (
        <div className={css.filterAlert}>
          <Alert severity="info">
            Drag to reorder is disabled while filtering. Product positions are
            relative to all products.{" "}
            <Button variant="text" size="sm" onClick={handleClearFilters}>
              Clear filters
            </Button>
          </Alert>
        </div>
      )}
    </div>
  );

  return (
    <>
      <SectionHead title={`Active products (${activeCount})`}>
        {!influencer?.isGroupShop && (
          <FAB
            variant={isCompact ? "standard" : "extended"}
            size="md"
            icon={<AddIcon />}
            label="Add Products"
            onClick={onClickAdd}
          />
        )}
      </SectionHead>

      {selection.selectionCount > 0 && (
        <BulkActionsToolbar
          isAdmin={isAdmin}
          selectedIds={selection.selectedIds}
          clearSelection={selection.clearSelection}
          maxSelection={selection.maxSelection}
          products={influencer.products}
          refresh={refresh}
        />
      )}

      <List
        productFilter={productFilter}
        emptyLabel="No Products"
        filter={activeProductFilter}
        influencer={influencer}
        isDragEnabled={
          !isFiltered &&
          !influencer.isGroupShop &&
          selection.selectionCount === 0
        }
        isFiltered={isFiltered}
        isAdmin={isAdmin}
        isGroupShop={influencer.isGroupShop}
        isSelectable={isAdmin || !influencer.isGroupShop}
        onClickAdd={onClickAdd}
        onClose={onClose}
        onReorder={handleReorder}
        refresh={refresh}
        salesCountMap={salesCountMap}
        selection={selection}
      />

      <div className={css.inactiveSection}>
        <Accordion.Root defaultValue={[]}>
          <Accordion.Item value="inactive" className={css.inactiveCard}>
            <Accordion.Header>
              <Accordion.Trigger className={css.inactiveTrigger}>
                {`Inactive products (${inactiveCount})`}
              </Accordion.Trigger>
            </Accordion.Header>
            <Accordion.Panel>
              <List
                emptyLabel="No Inactive Products"
                filter={inactiveProductFilter}
                influencer={influencer}
                isAdmin={isAdmin}
                isDragEnabled={false}
                isSelectable={isAdmin || !influencer.isGroupShop}
                onClose={onClose}
                refresh={refresh}
                salesCountMap={salesCountMap}
                selection={selection}
              />
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion.Root>
      </div>
    </>
  );
});

ShopBuilderProducts.propTypes = {
  onClose: PropTypes.func,
  route: PropTypes.string.isRequired,
  salesCountMap: PropTypes.object,
  setAddingProduct: PropTypes.func.isRequired,
};

export default ShopBuilderProducts;
